import { useState, useRef, useEffect } from 'react';

/**
 * Hook that detects movement completion and triggers arrival animation + haptic feedback.
 *
 * @param {Object} params
 * @param {Object|null} params.movementProgress - Current movement progress from regionCard
 * @param {Object|null} params.position - Current player position { x, y }
 * @param {Object} params.haptic - Haptic feedback object from useTelegramHaptic
 * @param {boolean} params.haptic.isAvailable
 * @param {Function} params.haptic.notificationOccurred
 * @param {Function|null} params.centerMapOnPlayer - Function to center map on player position
 * @returns {{ playerArrivalCell: Object|null }} - The cell currently animating arrival, or null
 */
export function useMovementArrival({ movementProgress, position, haptic, centerMapOnPlayer }) {
    const { isAvailable, notificationOccurred } = haptic;
    const [playerArrivalCell, setPlayerArrivalCell] = useState(null);
    const previousMovementProgress = useRef(null);

    useEffect(() => {
        // Check if movement just completed (had progress, now null)
        if (previousMovementProgress.current && !movementProgress && position) {
            console.log('[useMovementArrival] Movement completed, triggering arrival animation at:', position);
            setPlayerArrivalCell({ x: position.x, y: position.y, timestamp: Date.now() });

            // Trigger success haptic feedback on movement completion
            if (isAvailable) {
                try {
                    notificationOccurred('success');
                } catch (e) {
                    console.warn('[useMovementArrival] Movement completion haptic failed:', e);
                }
            }

            // Center map on new hero position after a brief delay for DOM to settle
            if (centerMapOnPlayer) {
                setTimeout(() => centerMapOnPlayer(), 100);
            }

            // Clear animation state after animation completes
            setTimeout(() => {
                setPlayerArrivalCell(null);
            }, 2000);
        }

        previousMovementProgress.current = movementProgress;
    }, [movementProgress, position, isAvailable, notificationOccurred, centerMapOnPlayer]);

    return { playerArrivalCell };
}
